import styled from "styled-components";
import { colors } from "../../../utils";
import { ASSETS } from "../../../utils/config";
import { sliderToOpacity } from "../../../utils/helpers";
import { cssUrl } from "../../../utils/cssUrl";
import { FAB_SIZE, FAB_MARGIN_XS } from "../../../components/HelpAssistant/fabPosition";
import { LABEL_TOGGLE_TOP_PX } from "./mobileViewWheelConfig";

/* M390-03 (mobile-390 sweep vY, MEASURED on staging bundle main.708ccddf.js
   @390x844): the persistent Help FAB (fixed, bottom-right, z-index 13000 —
   HelpAssistant/index.jsx, entirely first-party, NOT a 3rd-party iframe —
   this page just never reserved room for it) sat on top of the right-side
   segment controls at the bottom of this page's own scroll region. Same fix
   shape as the already-shipped Notifications/Capture FAB clearance (see
   FAB_CLEARANCE_PX there): reserve bottom scroll padding >= the FAB's reach +
   a gap, so the last row of content always scrolls clear of it. This page is
   phone-only (MobileView), so there is one constant, not a desktop/mobile
   split like Capture's Shell. */
export const FAB_CLEARANCE_PX = FAB_SIZE + FAB_MARGIN_XS + 24; // 56 + 16 + 24 = 96

export const Wrapper = styled.div`
  /* BUG-607a (Gordon 2026-08-19, wheel/4783, responsive view): a gray bar ran
     top-to-bottom down the RIGHT edge, disturbing layout and even bleeding
     INSIDE the Segment Box. \`100vw\` is the classic scrollbar-gutter bug: it
     is measured against the FULL viewport (including the browser's own
     vertical scrollbar track), while \`100%\` of this Wrapper's own parent
     excludes it — so on any browser/OS that renders a native scrollbar
     (this element's OWN scrollbar is hidden below, but that hides only ITS
     bar, not an ancestor's), a 100vw-wide child sits wider than the visible
     content area by exactly the scrollbar's width, and the sliver of
     underlying page background it exposes on the right reads as a stray gray
     strip — one that can visually intrude on ANY element positioned near that
     edge, including the Segment Box panel. \`100%\` removes the whole bug
     class: it always matches the true available content width. */
  width: 100%;
  max-height: 100svh;
  overflow: scroll;
  padding-bottom: calc(${FAB_CLEARANCE_PX}px + env(safe-area-inset-bottom));
  box-sizing: border-box;

  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  /* Hide scrollbar for WebKit browsers (Chrome, Safari) */
  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  /* Hide scrollbar for Firefox */
  scrollbar-width: none;
`;

export const Header = styled.div`
background-color:  #ffffff80;
  margin-top: ${({ hideView }) => (hideView ? '-15vh' : '0svh')};
  margin-top: ${({ hideView }) => (hideView ? '-18svh' : '0svh')};
  transition: margin-top 0.3s ease-in-out;
  position: relative;
  z-index: 100;
`;

export const ContentView = styled.div`
  position: relative;
  z-index: 0;
  display: flex;
  flex-direction: column;
  align-items: center;

  min-height: calc(100vh - 23vh);
  min-height: calc(100svh - 18svh);
`;

export const Background = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  /* z-index: -1; */
  width: 100%;
  background: ${({ currentBg, bgWallpaper, color }) => {
    if ((currentBg === 2 || currentBg === 3) && bgWallpaper !== "") {
      return currentBg === 2 ? cssUrl(bgWallpaper) : bgWallpaper;
    } else if (currentBg === 0 && !color) {
      return cssUrl(ASSETS.segmentEditor.DefaultWallpaperForViewWheel);
    }
    return color;
  }};
  object-fit: contain;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  background-size: cover; /* Change from 100% 100% to cover */
  opacity: ${({ wallpaperTransparency }) =>
    sliderToOpacity(wallpaperTransparency)};
  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: ${({ wallpaperHueColor }) =>
      wallpaperHueColor ?? "#ffffff"};
    opacity: ${({ wallpaperHuePercentage }) =>
      ((wallpaperHuePercentage ?? 0) / 100)};
    mix-blend-mode: multiply;
    pointer-events: none;
  }
`;

export const IconsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-evenly;
  background-color: #ffffffe5;
  border-bottom: 1px solid #e1e1e1;
  height: 6vh;
  height: 8svh;
`;

export const IconWrapper = styled.div`
  height: 42px;
  width: 42px;
  background-color: ${colors.white};
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 3px;
  cursor: pointer;
  border-radius: 4px;
  padding: 8px;
  transition: box-shadow 0.2s ease;
  cursor: pointer;

  &:active {
    box-shadow: 0 0 10px rgba(0, 0, 0, 0.2);
  }

  /* T22.221: the phone Edit tile carries a visible "Edit on computer" caption
     beside its pencil, so it grows to fit instead of staying a 42px square.
     Below 360px the row has no room for it (measured: right edge 342px at a
     320px viewport), so it stays the plain 42px pencil there. */
  ${({ $hasCaption }) =>
    $hasCaption &&
    `
    @media (min-width: 360px) {
      width: auto;
      gap: 4px;
      padding: 4px 8px 4px 6px;
      border: 1px solid #e1e1e1;
    }
  `}
`;

export const IconCaption = styled.span`
  font-size: 11px;
  line-height: 1.15;
  font-weight: 600;
  color: #123051;
  max-width: 58px;
  text-align: left;
  white-space: normal;

  @media (max-width: 359.98px) {
    display: none;
  }
`;

export const WheelView = styled.div`
  display: grid;
  align-items: center;
  justify-content: center;
  position: relative;
`;

export const SegmentListButton = styled.div`
  background-color: #ffffffc2;
  border-radius: 20px;
  padding: 8px 16px 8px 16px;
  gap: 10px;
  border-radius: 100px;
  align-self: flex-end;
  margin-bottom: -20px;
  z-index: 20;
`;

export const NotificationBar = styled.div`
  width: 100%;
  margin-bottom: 1rem;
  position: absolute;
  top: 0;
  background-color: #ffffffe5;
  border-radius: 0px 0px 24px 24px;
  padding: 16px;
  gap: 10px;
  display: flex;
  flex-direction: column;
  z-index: 500;
`;

export const TextContainer = styled.div`
  background: var(--Green-500, #b9d645);
  padding: 12px 20px 12px 20px;
  gap: 20px;
  border-radius: 8px;
  box-shadow: 0px 0px 9.3px 0px #3b3b3b1c;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const ToggleLabelIcon = styled.img`
  margin: 10px 10px 10px 10px;
`;

// T22.123: was top:12svh — inside the 2-3 o'clock rim-pill band, so the icon
// covered Segment 3 on a 12-segment wheel at 390px. It now sits in a lane at
// the top of ContentView; useRimPillClearance keeps every pill below it.
export const ToggleLabelIconContainer = styled.div`
  position: absolute;
  right: 4vw;
  top: ${LABEL_TOGGLE_TOP_PX}px;
  z-index: 10;
  cursor: pointer;
  background-color: white;
  border-radius: 5px;
`;

// T22.224 (UX-24): header strip of the phone comments bottom sheet — a
// grabber cue plus a 44px close control (touch-target minimum).
export const CommentsSheetHeader = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 44px;
  padding: 0 4px;
`;

export const CommentsSheetGrabber = styled.span`
  width: 40px;
  height: 4px;
  border-radius: 2px;
  background: #d0d4dc;
`;

export const CommentsSheetClose = styled.button`
  position: absolute;
  right: 4px;
  top: 0;
  width: 44px;
  height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colors.darkGrey};
  cursor: pointer;
  border-radius: 50%;
`;
